import { useState } from "react";
import { motion } from "framer-motion";
import { X, Save, Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import type { ChallengeTemplate, Difficulty } from "@/core/data/types";
import { LEVELS, type InterviewQuestion } from "@/content/interview";
import { TRIAGE_TYPES, type TriageLens, type TriageType } from "@/content/triage";
import { useCustomContentStore, slugId, useCategories, useInterviewRoles, useInterviewTypes } from "@/store/customContentStore";
import { useLibraryContentStore } from "@/store/libraryContentStore";
import { useDesignDocContentStore, slugDocId, useDocSections } from "@/store/designDocContentStore";
import type { DocFormat, DocSection } from "@/content/designDocs";
import RichTextEditor from "@/components/RichTextEditor";

const DIFFS: Difficulty[] = ["Easy", "Medium", "Hard"];

const inputCls =
  "w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted">{label}</span>
      {children}
    </label>
  );
}

/** Editable list of strings (constraints, hints, ideal points, tags). */
function StringList({ label, values, onChange, placeholder }: { label: string; values: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  return (
    <Field label={label}>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input
              value={v}
              onChange={(e) => onChange(values.map((x, n) => (n === i ? e.target.value : x)))}
              placeholder={placeholder}
              className={inputCls}
            />
            <button onClick={() => onChange(values.filter((_, n) => n !== i))} aria-label="Remove" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border text-muted hover:text-amber-400">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <button onClick={() => onChange([...values, ""])} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-1.5 text-sm text-muted hover:border-primary/50 hover:text-text">
          <Plus className="h-4 w-4" /> Add item
        </button>
      </div>
    </Field>
  );
}

function Shell({ title, onClose, onSave, error, children }: { title: string; onClose: () => void; onSave: () => void; error: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} className="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h3 className="font-display text-lg font-semibold text-text">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="grid h-8 w-8 place-items-center rounded-lg border border-border text-muted hover:bg-surface-2 hover:text-text">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">{children}{error && <p className="text-sm text-amber-400">{error}</p>}</div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted hover:text-text">Cancel</button>
          <button onClick={onSave} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.02]">
            <Save className="h-4 w-4" /> Save
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export function ChallengeEditor({ template, onClose }: { template: ChallengeTemplate | null; onClose: () => void }) {
  const saveChallenge = useCustomContentStore((s) => s.saveChallenge);
  const categories = useCategories();

  const [t, setT] = useState<ChallengeTemplate>(
    template ?? {
      id: "",
      disciplineIds: [],
      title: "",
      task: "",
      question: "",
      constraints: [],
      hints: [],
      idealPoints: [],
      exampleApproach: "",
      deliverable: "",
      difficulty: "Medium",
      estMinutes: 25,
      tags: [],
    }
  );
  const [error, setError] = useState("");
  const set = <K extends keyof ChallengeTemplate>(k: K, v: ChallengeTemplate[K]) => setT((p) => ({ ...p, [k]: v }));

  function toggleDiscipline(id: string) {
    set("disciplineIds", t.disciplineIds.includes(id) ? t.disciplineIds.filter((x) => x !== id) : [...t.disciplineIds, id]);
  }

  function save() {
    if (!t.title.trim() || !t.task.trim()) return setError("A title and a task are required.");
    if (t.disciplineIds.length === 0) return setError("Pick at least one discipline.");
    const clean = (a: string[]) => a.map((s) => s.trim()).filter(Boolean);
    saveChallenge({
      ...t,
      id: t.id || slugId("chal", t.title),
      title: t.title.trim(),
      task: t.task.trim(),
      constraints: clean(t.constraints),
      hints: clean(t.hints),
      idealPoints: clean(t.idealPoints),
      tags: clean(t.tags),
      deliverable: t.deliverable?.trim() || undefined,
    });
    onClose();
  }

  return (
    <Shell title={template ? "Edit challenge" : "Add a challenge"} onClose={onClose} onSave={save} error={error}>
      <Field label="Disciplines">
        <div className="flex flex-wrap gap-1.5">
          {categories.map((c) => {
            const on = t.disciplineIds.includes(c.id);
            return (
              <button key={c.id} onClick={() => toggleDiscipline(c.id)} className={["rounded-md border px-2 py-0.5 text-xs transition-colors", on ? "border-primary bg-primary/15 text-primary" : "border-border text-muted hover:text-text"].join(" ")}>
                {c.name}
              </button>
            );
          })}
        </div>
      </Field>
      <Field label="Title"><input value={t.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="Design the Core Loop" /></Field>
      <Field label="Task (what the user produces)"><RichTextEditor value={t.task} onChange={(md) => set("task", md)} placeholder="What the user must produce…" minHeight="5rem" /></Field>
      <Field label="Guiding question"><input value={t.question} onChange={(e) => set("question", e.target.value)} className={inputCls} /></Field>
      <Field label="Deliverable (the artifact to make — optional)"><input value={t.deliverable ?? ""} onChange={(e) => set("deliverable", e.target.value)} className={inputCls} placeholder="A one-page design doc / a spreadsheet / a pitch deck…" /></Field>
      <StringList label="Constraints" values={t.constraints} onChange={(v) => set("constraints", v)} placeholder="One constraint" />
      <StringList label="Hints" values={t.hints} onChange={(v) => set("hints", v)} placeholder="A nudge when stuck" />
      <StringList label="Key points (self-assessment rubric)" values={t.idealPoints} onChange={(v) => set("idealPoints", v)} placeholder="What a strong answer covers" />
      <Field label="Example approach"><RichTextEditor value={t.exampleApproach} onChange={(md) => set("exampleApproach", md)} placeholder="A model way to approach it…" minHeight="6rem" /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Difficulty">
          <select value={t.difficulty} onChange={(e) => set("difficulty", e.target.value as Difficulty)} className={inputCls}>
            {DIFFS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="Est. minutes"><input type="number" value={t.estMinutes} onChange={(e) => set("estMinutes", Number(e.target.value) || 0)} className={inputCls} /></Field>
      </div>
      <StringList label="Tags" values={t.tags} onChange={(v) => set("tags", v)} placeholder="core-loop" />
    </Shell>
  );
}

export function QuestionEditor({ question, onClose }: { question: InterviewQuestion | null; onClose: () => void }) {
  const saveQuestion = useCustomContentStore((s) => s.saveQuestion);
  const roles = useInterviewRoles();
  const types = useInterviewTypes();

  const [q, setQ] = useState<InterviewQuestion>(
    question ?? {
      id: "",
      roleId: roles[0]?.id ?? "",
      typeId: types[0]?.id ?? "",
      prompt: "",
      idealPoints: [],
      exampleAnswer: "",
      deliverable: "",
      difficulty: "Medium",
      level: "",
      estMinutes: 15,
      tags: [],
    }
  );
  const [error, setError] = useState("");
  const set = <K extends keyof InterviewQuestion>(k: K, v: InterviewQuestion[K]) => setQ((p) => ({ ...p, [k]: v }));

  function save() {
    if (!q.prompt.trim()) return setError("A prompt is required.");
    if (!q.roleId || !q.typeId) return setError("Pick a role and a type.");
    const clean = (a: string[]) => a.map((s) => s.trim()).filter(Boolean);
    saveQuestion({
      ...q,
      id: q.id || slugId("q", q.prompt.slice(0, 24)),
      prompt: q.prompt.trim(),
      idealPoints: clean(q.idealPoints),
      tags: clean(q.tags),
      deliverable: q.deliverable?.trim() || undefined,
      level: q.level || undefined,
    });
    onClose();
  }

  return (
    <Shell title={question ? "Edit question" : "Add a question"} onClose={onClose} onSave={save} error={error}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Role">
          <select value={q.roleId} onChange={(e) => set("roleId", e.target.value)} className={inputCls}>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
        <Field label="Type">
          <select value={q.typeId} onChange={(e) => set("typeId", e.target.value)} className={inputCls}>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Prompt"><textarea value={q.prompt} onChange={(e) => set("prompt", e.target.value)} className={`${inputCls} h-24 resize-y`} /></Field>
      <Field label="Deliverable (for take-home/portfolio — optional)"><input value={q.deliverable ?? ""} onChange={(e) => set("deliverable", e.target.value)} className={inputCls} placeholder="A Google Doc / Google Sheet / flowchart / pitch deck / bring your prototype…" /></Field>
      <Field label="Designer level (optional — defaults from difficulty)">
        <select value={q.level ?? ""} onChange={(e) => set("level", e.target.value)} className={inputCls}>
          <option value="">Auto (from difficulty)</option>
          {LEVELS.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </Field>
      <StringList label="Ideal points (self-assessment)" values={q.idealPoints} onChange={(v) => set("idealPoints", v)} placeholder="What a strong answer covers" />
      <Field label="Example answer"><RichTextEditor value={q.exampleAnswer} onChange={(md) => set("exampleAnswer", md)} placeholder="A strong model answer…" minHeight="7rem" /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Difficulty">
          <select value={q.difficulty} onChange={(e) => set("difficulty", e.target.value as Difficulty)} className={inputCls}>
            {DIFFS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="Est. minutes"><input type="number" value={q.estMinutes} onChange={(e) => set("estMinutes", Number(e.target.value) || 0)} className={inputCls} /></Field>
      </div>
      <StringList label="Tags" values={q.tags} onChange={(v) => set("tags", v)} placeholder="behavioral" />
    </Shell>
  );
}

export function LensEditor({ lens, onClose }: { lens: TriageLens | null; onClose: () => void }) {
  const saveLens = useCustomContentStore((s) => s.saveLens);
  const categories = useCategories();
  const subSessions = useLibraryContentStore((s) => s.subSessions);

  const [l, setL] = useState<TriageLens>(
    lens ?? { id: "", name: "", blurb: "", appliesTo: ["mechanic", "system", "feature"], questions: [""], disciplineId: "", libraryEntryId: "" }
  );
  const [error, setError] = useState("");
  const set = <K extends keyof TriageLens>(k: K, v: TriageLens[K]) => setL((p) => ({ ...p, [k]: v }));

  function toggleType(t: TriageType) {
    set("appliesTo", l.appliesTo.includes(t) ? l.appliesTo.filter((x) => x !== t) : [...l.appliesTo, t]);
  }

  function save() {
    if (!l.name.trim()) return setError("A lens name is required.");
    const questions = l.questions.map((q) => q.trim()).filter(Boolean);
    if (questions.length === 0) return setError("Add at least one question.");
    if (l.appliesTo.length === 0) return setError("Pick at least one subject type.");
    saveLens({
      ...l,
      id: l.id || slugId("lens", l.name),
      name: l.name.trim(),
      blurb: l.blurb.trim(),
      questions,
      disciplineId: l.disciplineId || undefined,
      libraryEntryId: l.libraryEntryId || undefined,
    });
    onClose();
  }

  return (
    <Shell title={lens ? "Edit lens" : "Add a Triage lens"} onClose={onClose} onSave={save} error={error}>
      <Field label="Lens name"><input value={l.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="e.g. Replayability" /></Field>
      <Field label="One-line blurb"><input value={l.blurb} onChange={(e) => set("blurb", e.target.value)} className={inputCls} placeholder="What this lens interrogates" /></Field>
      <Field label="Applies to">
        <div className="flex flex-wrap gap-1.5">
          {TRIAGE_TYPES.map((t) => {
            const on = l.appliesTo.includes(t.id);
            return (
              <button key={t.id} onClick={() => toggleType(t.id)} className={["rounded-md border px-2.5 py-1 text-xs transition-colors", on ? "border-primary bg-primary/15 text-primary" : "border-border text-muted hover:text-text"].join(" ")}>
                {t.name}
              </button>
            );
          })}
        </div>
      </Field>
      <StringList label="Questions" values={l.questions} onChange={(v) => set("questions", v)} placeholder="A probing question…" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Practice link (discipline — optional)">
          <select value={l.disciplineId ?? ""} onChange={(e) => set("disciplineId", e.target.value)} className={inputCls}>
            <option value="">None</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.group} · {c.name}</option>)}
          </select>
        </Field>
        <Field label="Study link (library section — optional)">
          <select value={l.libraryEntryId ?? ""} onChange={(e) => set("libraryEntryId", e.target.value)} className={inputCls}>
            <option value="">None</option>
            {subSessions.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        </Field>
      </div>
    </Shell>
  );
}

export function DocSectionEditor({ section, onClose }: { section: DocSection | null; onClose: () => void }) {
  const saveSection = useDesignDocContentStore((s) => s.saveSection);
  const [s, setS] = useState<DocSection>(section ?? { id: "", title: "", guidance: "" });
  const [error, setError] = useState("");

  function save() {
    if (!s.title.trim()) return setError("A section title is required.");
    saveSection({ id: s.id || slugDocId("sec", s.title), title: s.title.trim(), guidance: s.guidance.trim() });
    onClose();
  }

  return (
    <Shell title={section ? "Edit section" : "Add a section"} onClose={onClose} onSave={save} error={error}>
      <Field label="Section title"><input value={s.title} onChange={(e) => setS((p) => ({ ...p, title: e.target.value }))} className={inputCls} placeholder="e.g. Accessibility Plan" /></Field>
      <Field label="Guidance (placeholder shown for an empty section)"><textarea value={s.guidance} onChange={(e) => setS((p) => ({ ...p, guidance: e.target.value }))} className={`${inputCls} h-24 resize-y`} placeholder="What the author should write here." /></Field>
    </Shell>
  );
}

export function DocFormatEditor({ format, onClose }: { format: DocFormat | null; onClose: () => void }) {
  const saveFormat = useDesignDocContentStore((s) => s.saveFormat);
  const sections = useDocSections();
  const byId = Object.fromEntries(sections.map((x) => [x.id, x]));

  const [f, setF] = useState<DocFormat>(format ?? { id: "", name: "", blurb: "", sectionIds: [] });
  const [error, setError] = useState("");
  const [toAdd, setToAdd] = useState("");
  const set = <K extends keyof DocFormat>(k: K, v: DocFormat[K]) => setF((p) => ({ ...p, [k]: v }));

  const available = sections.filter((x) => !f.sectionIds.includes(x.id));

  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= f.sectionIds.length) return;
    const next = [...f.sectionIds];
    [next[i], next[j]] = [next[j], next[i]];
    set("sectionIds", next);
  }

  function save() {
    if (!f.name.trim()) return setError("A format name is required.");
    if (f.sectionIds.length === 0) return setError("Add at least one section.");
    saveFormat({ id: f.id || slugDocId("fmt", f.name), name: f.name.trim(), blurb: f.blurb.trim(), sectionIds: f.sectionIds });
    onClose();
  }

  return (
    <Shell title={format ? "Edit format" : "Add a format"} onClose={onClose} onSave={save} error={error}>
      <Field label="Format name"><input value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="e.g. Vertical Slice Brief" /></Field>
      <Field label="Blurb"><input value={f.blurb} onChange={(e) => set("blurb", e.target.value)} className={inputCls} placeholder="One line describing this format" /></Field>
      <Field label="Sections (in order)">
        <div className="space-y-2">
          {f.sectionIds.length === 0 && <p className="text-sm text-muted">No sections yet — add some below.</p>}
          {f.sectionIds.map((id, i) => (
            <div key={id} className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-1.5">
              <span className="flex-1 truncate text-sm text-text">{byId[id]?.title ?? id}</span>
              <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="grid h-7 w-7 place-items-center rounded-md text-muted hover:text-text disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === f.sectionIds.length - 1} aria-label="Move down" className="grid h-7 w-7 place-items-center rounded-md text-muted hover:text-text disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
              <button onClick={() => set("sectionIds", f.sectionIds.filter((x) => x !== id))} aria-label="Remove" className="grid h-7 w-7 place-items-center rounded-md text-muted hover:text-amber-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <select value={toAdd} onChange={(e) => setToAdd(e.target.value)} className={inputCls}>
              <option value="">Add a section…</option>
              {available.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
            </select>
            <button onClick={() => { if (toAdd) { set("sectionIds", [...f.sectionIds, toAdd]); setToAdd(""); } }} disabled={!toAdd} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm font-medium text-text hover:bg-border/40 disabled:opacity-40">
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        </div>
      </Field>
    </Shell>
  );
}
